import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PERMISSIONS } from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { SelectField } from '../../../components/forms/SelectField';
import { PageSpinner } from '../../../components/PageSpinner';
import { useAuth } from '../../../features/auth/auth-context';
import { customersApi } from '../../../features/customers/customers.api';
import { mandatesApi } from '../../../features/mandates/mandates.api';
import { useSessionQuery as useQuery } from '../../../hooks/useSessionQuery';
import { toDisplayMessage } from '../../../lib/error-message';
import { paths, routeTo } from '../../../routes/paths';
import styles from '../AppPages.module.css';

export function MandateSetupPage(): React.JSX.Element {
  const { contractId = '' } = useParams<{ contractId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [bankAccountId, setBankAccountId] = useState('');

  const existingMandate = useQuery({
    queryKey: ['customer', 'contracts', contractId, 'mandate'],
    queryFn: () => mandatesApi.getContractMandate(contractId),
  });

  const supportedBanks = useQuery({
    queryKey: ['customer', 'supported-banks'],
    queryFn: () => mandatesApi.listSupportedBanks(),
  });

  const bankAccounts = useQuery({
    queryKey: ['customer', 'bank-accounts'],
    queryFn: () => customersApi.listBankAccounts(),
  });

  const create = useMutation({
    mutationFn: () =>
      mandatesApi.createContractMandate(contractId, {
        bankAccountId,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: async (mandate) => {
      await queryClient.invalidateQueries({ queryKey: ['customer', 'contracts', contractId] });
      void navigate(routeTo.customerMandate(mandate.id), { replace: true });
    },
  });

  useEffect(() => {
    if (existingMandate.isSuccess && existingMandate.data !== null) {
      void navigate(routeTo.customerMandate(existingMandate.data.id), { replace: true });
    }
  }, [existingMandate.data, existingMandate.isSuccess, navigate]);

  if (existingMandate.isPending || supportedBanks.isPending || bankAccounts.isPending) {
    return <PageSpinner label="Loading mandate setup" />;
  }

  if (existingMandate.isError) {
    return <Alert tone="error">{toDisplayMessage(existingMandate.error)}</Alert>;
  }

  if (existingMandate.data !== null) {
    return <PageSpinner label="Redirecting to mandate" />;
  }

  const banksError = supportedBanks.isError ? toDisplayMessage(supportedBanks.error) : null;
  const accountsError = bankAccounts.isError ? toDisplayMessage(bankAccounts.error) : null;

  const activeBankCodes = new Set(
    (supportedBanks.data ?? [])
      .filter((bank) => bank.status === 'ACTIVE')
      .map((bank) => bank.bankCode),
  );

  const eligibleAccounts =
    bankAccounts.data?.filter(
      (account) => account.status === 'ACTIVE' && activeBankCodes.has(account.bankCode),
    ) ?? [];

  const canCreate = can(PERMISSIONS.MANDATES_CREATE);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Set up Direct Debit</h1>
        <p className={styles.pageSubtitle}>
          Select the bank account you will use for repayments. Only supported commercial banks are
          eligible.
        </p>
      </header>

      <p>
        <Link to={routeTo.customerContract(contractId)}>Back to contract</Link>
      </p>

      {banksError !== null && <Alert tone="error">{banksError}</Alert>}
      {accountsError !== null && <Alert tone="error">{accountsError}</Alert>}
      {create.isError && <Alert tone="error">{toDisplayMessage(create.error)}</Alert>}

      <section className={styles.card} aria-labelledby="supported-banks-heading">
        <h2 className={styles.cardTitle} id="supported-banks-heading">
          Supported banks
        </h2>

        {supportedBanks.isSuccess && supportedBanks.data.length === 0 && (
          <p className={styles.empty}>No supported banks are available right now.</p>
        )}

        {supportedBanks.isSuccess && supportedBanks.data.length > 0 && (
          <ul className={styles.chipList}>
            {supportedBanks.data
              .filter((bank) => bank.status === 'ACTIVE')
              .map((bank) => (
                <li key={bank.bankCode} className={styles.chip}>
                  {bank.bankName} ({bank.bankCode})
                </li>
              ))}
          </ul>
        )}
      </section>

      <section className={styles.card} aria-labelledby="select-account-heading">
        <h2 className={styles.cardTitle} id="select-account-heading">
          Bank account
        </h2>

        {bankAccounts.isSuccess && eligibleAccounts.length === 0 && (
          <p className={styles.empty}>
            No eligible bank accounts found. Add an account at a supported bank first.
          </p>
        )}

        {bankAccounts.isSuccess && eligibleAccounts.length > 0 && (
          <form
            className={styles.filters}
            onSubmit={(event) => {
              event.preventDefault();
              if (canCreate) {
                create.mutate();
              }
            }}
          >
            <SelectField
              label="Account for Direct Debit"
              name="bankAccountId"
              value={bankAccountId}
              required
              onChange={(event) => {
                setBankAccountId(event.target.value);
              }}
            >
              <option value="">Select an account</option>
              {eligibleAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.bankNameSnapshot} — {account.accountName} ({account.accountNumberMasked})
                </option>
              ))}
            </SelectField>

            {canCreate ? (
              <Button type="submit" disabled={create.isPending || bankAccountId === ''}>
                Create mandate
              </Button>
            ) : (
              <Alert tone="warning">You do not have permission to create a mandate.</Alert>
            )}
          </form>
        )}

        {bankAccounts.isSuccess && eligibleAccounts.length === 0 && (
          <p>
            <Link to={paths.customerBankAccounts}>Manage bank accounts</Link>
          </p>
        )}
      </section>
    </div>
  );
}
